import { NavigationItem } from '@app/components/navigation/NavigationItem';
import { Button } from '@app/components/various/Button';
import { ChevronDown } from '@app/components/various/Icons';
import { getHasActiveNavigationItemChild, getIsMenuItemActive } from '@app/logic/navigation';
import { useExpander } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import { Navigation } from '@typings';
import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

interface Props {
  item: Navigation.FolderItem | Navigation.PageItem;
  activeClassName: string;
  className: string;
}

export const ExpandableItem = ({ item, ...itemProps }: Props) => {
  const { pathname } = useLocation();
  const hasActivePageChild = getHasActiveNavigationItemChild(item, pathname);
  const { t } = useTranslation(['common']);
  const [isExpanded, setIsExpanded] = React.useState(hasActivePageChild);

  const handleToggle = () => setIsExpanded(current => !current);

  const { sectionClassName, sectionProps, triggerProps } = useExpander({ isExpanded });

  if (!isDefined(item.children)) {
    return null;
  }

  const isMenuActive = item.type !== 'folder' && getIsMenuItemActive(item.url, pathname);

  return (
    <li>
      <div
        className={cx(itemProps.className, 'flex justify-between', {
          [itemProps.activeClassName]: isMenuActive,
        })}
      >
        <NavigationItem
          item={item}
          activeClassName="body-1"
          className="w-full overflow-hidden text-ellipsis rounded-regular focus-visible:focus-outline"
        />
        <Button {...triggerProps} onClick={handleToggle} variant="text" color="secondary" size="medium" className="shrink-0">
          <span className="sr-only">{t('common:more_in', { parent: item.label })}</span>
          <ChevronDown className={cx('transition-transform', { '-rotate-180': isExpanded })} />
        </Button>
      </div>
      <div className={cx(sectionClassName, { 'overflow-hidden': !isExpanded })} {...sectionProps}>
        <ul className="-m-1 ml-1 mt-0 flex flex-col gap-1 overflow-hidden p-1">
          {item.children.map(child => (
            <li key={child.label}>
              <NavigationItem item={child} {...itemProps} />
            </li>
          ))}
        </ul>
      </div>
    </li>
  );
};
